import {
  calculatePolicyInflationProtection,
  getInflationProtectionYears,
  getInflationRateFromQuote,
} from '@/utils/inflationParser';
import {
  CareEnvironment,
  FundingPolicyType,
  calcClientDerivatives,
  defaultsRetroactiveWaitingPeriodReimbursement,
  fundingPolicyTypeDefs,
  getQuotePayYears,
  isCareMattersFamily,
  isCareMattersAnnuity,
  requiresClaimProjectionEngine,
  careMattersAnnuityAccelerationMonths,
  resolveFacilityCareWaitingPeriodDays,
  resolveHomeCareWaitingPeriodDays,
  toStateAbbreviation,
} from '@shared';
import { calcMaxAchievableCoverage } from '@shared/calculations/roi/maxAchievableCoverage';
import { cloneDeep } from 'lodash';
import { Quote } from '../model/quotesModel';
import { getPolicyTypeFromProductName } from './findPartnerPolicyFundingSourceId';
import { createShortTermCareFundingSource } from '@/features/shop-insurance/lib/create-short-term-care-funding-source';

/**
 * Helper function to find discount policy type from any care environment that has a discount
 */
function findDiscountPolicyType(
  clientLocalCareEnvironmentCosts?: LocalCareEnvironmentCosts,
): number | undefined {
  if (!clientLocalCareEnvironmentCosts) {
    return undefined;
  }

  const careEnvironments: CareEnvironment[] = [
    CareEnvironment.home,
    CareEnvironment.assistedLiving,
    CareEnvironment.fullCareFacility,
    CareEnvironment.independentLiving,
  ];

  for (const careEnvironment of careEnvironments) {
    const environmentCost = clientLocalCareEnvironmentCosts[careEnvironment];
    if (environmentCost?.discount?.policyType) {
      return environmentCost.discount.policyType;
    }
  }

  return undefined;
}

export const createPolicyFundingSourceFromQuote = (
  quote: Quote,
  client: Client,
  isAdvisorSession = false,
  advisorId?: string,
  updatedQuote?: Quote | null,
  illustrationPdfUrl?: string | null,
): PolicyFundingSource => {
  const calculateDailyBenefit = (quote: Quote): number => {
    if (
      quote.ltcBenefitMonthlyIndividual &&
      !isNaN(quote.ltcBenefitMonthlyIndividual) &&
      quote.ltcBenefitMonthlyIndividual > 0
    ) {
      return quote.ltcBenefitMonthlyIndividual / 30;
    }

    // Fallback: try using annual benefit if available
    if (
      quote.ltcBenefitAnnualIndividual &&
      !isNaN(quote.ltcBenefitAnnualIndividual) &&
      quote.ltcBenefitAnnualIndividual > 0
    ) {
      console.warn(
        `Using annual benefit for daily calculation for quote ${quote.insuranceQuoteId}`,
      );
      return quote.ltcBenefitAnnualIndividual / 365;
    }

    // Fallback: estimate from total LTC benefit
    // For most policies, assume 4-6 year benefit period as reasonable default
    if (quote.ltcBenefitTotal && !isNaN(quote.ltcBenefitTotal) && quote.ltcBenefitTotal > 0) {
      // Estimate 5-year benefit period: total benefit / (5 years * 365 days)
      const FIVE_YEARS_IN_DAYS = 1825;
      const estimatedDailyBenefit = quote.ltcBenefitTotal / FIVE_YEARS_IN_DAYS;
      console.warn(
        `ltcBenefitMonthlyIndividual missing for quote ${quote.insuranceQuoteId}. Estimated daily benefit from total: $${estimatedDailyBenefit.toFixed(2)}/day`,
      );
      return estimatedDailyBenefit;
    }

    // Final fallback: set to 0 to prevent NaN
    console.error(
      `Unable to calculate daily benefit for quote ${quote.insuranceQuoteId}. Setting to 0.`,
    );
    return 0;
  };

  const dailyBenefit = calculateDailyBenefit(quote);

  const isJointPolicy = quote.policyType === 'Joint Life';
  const policyType = getPolicyTypeFromProductName(quote.productName, isJointPolicy);
  const isSimpleInflation = quote.inflationBenefitOption?.toLowerCase().includes('simple') ?? false;

  const getIsIndemnityPolicyPayment = (quote: Quote): boolean => {
    if (quote.carrierName === 'OneAmerica') {
      return false;
    }
    if (policyType === FundingPolicyType.moneyGuardFixedAdvantage) {
      return false;
    }
    if (quote.carrierName === 'Lincoln Financial') {
      return true;
    }

    if (quote.carrierName === 'New York Life') {
      return false;
    }
    return true;
  };

  // Check if this is an unlimited policy (ltcBenefitTotal = 0 indicates unlimited)
  const isUnlimitedPolicy = quote.ltcBenefitTotal === 0;

  const inflationRate = getInflationRateFromQuote(quote);
  const inflationProtectionYears = getInflationProtectionYears(quote);
  const aobInflationParsed =
    quote.aobInflation && quote.aobInflation !== 'None' ? parseFloat(quote.aobInflation) : null;
  const cobInflationParsed =
    quote.cobInflation && quote.cobInflation !== 'None' ? parseFloat(quote.cobInflation) : null;

  // Asset Flex quotes carry the AOB / EOB benefit periods as unit-bearing
  // strings: "N Years", "N Months", "N/A" (no Extension pool), or "Lifetime".
  // Convert to whole months so the projection's duration model can size each
  // pool from monthly benefit × duration; without these the strategy falls back
  // to legacy dollar pools. The unit MUST be honored — "25 Months" is 25, not
  // 25×12. "Lifetime"/unparseable returns null (duration model can't size an
  // unbounded pool, so it falls back to the stored dollar pool).
  const parseBenefitDurationMonths = (value: string | undefined): number | null => {
    if (!value) return null;
    const v = value.trim();
    if (/n\/a|none/i.test(v)) return 0;
    if (/lifetime|unlimited/i.test(v)) return null;
    const num = parseFloat(v);
    if (isNaN(num) || num <= 0) return null;
    if (/month/i.test(v)) return Math.round(num);
    // "N Years" (or a bare number — the carrier source prints years).
    return Math.round(num * 12);
  };
  // Asset Flex, Asset Care and Securian SecureCare all size their AOB / COB
  // pools from monthly benefit × benefit period, so map the quote's duration
  // strings into the duration fields the projection's duration model reads.
  // ("N/A" COB → 0 so an un-elected EOB rider still yields a clean duration
  // model; "Lifetime" → null so the legacy/unlimited pool handling takes over.)
  const usesDurationDerivedPools =
    policyType === FundingPolicyType.nylAssetFlex ||
    policyType === FundingPolicyType.assetCare ||
    policyType === FundingPolicyType.jointAssetCare;

  // Securian SecureCare III/IV also size their pools from monthly benefit ×
  // duration, but their quotes carry NO aobDuration / cobDuration — those two
  // columns are null on every SecureCare row. The quote gives only
  // `ltcBenefitDurationMonths`, the TOTAL benefit period (48/60/72/84/96 = the
  // 4-8 year options), so the acceleration/extension split has to be recovered.
  //
  // Recover it per quote from the product's own construction: the acceleration
  // pool IS the face amount, so `face / max monthly` is that pool's length in
  // months. Deriving it per row rather than assuming Securian's usual 24 keeps
  // each policy self-consistent with the quote it came from — the pools always
  // reconstruct the quoted face and total, whatever the acceleration period.
  //
  // Without this every quote-added SecureCare policy lands with both durations
  // null, so deriveAtIssuePools falls back to the face amount alone and the
  // extension pool collapses to the (also null) COB dollar — silently, since
  // hasMixedModelFields only fires when exactly ONE duration is missing.
  const isSecureCare =
    policyType === FundingPolicyType.secureCareThree ||
    policyType === FundingPolicyType.secureCareFour;

  // Both-or-neither: a partial split would put the record on the mixed-model
  // path, which is worse than the legacy dollar-pool fallback it already had.
  const deriveSecureCareDurations = (): { acceleration: number; eob: number } | null => {
    const totalMonths = quote.ltcBenefitDurationMonths;
    const monthlyBenefit = quote.ltcBenefitMonthlyIndividual;
    const faceAmount = quote.deathBenefitInitial;
    if (!totalMonths || !monthlyBenefit || !faceAmount) return null;

    const acceleration = Math.round(faceAmount / monthlyBenefit);
    // An acceleration period at or beyond the total leaves no extension pool and
    // signals the face/monthly ratio can't be trusted (e.g. a re-priced quote,
    // which scales death benefit and LTC by different factors).
    if (acceleration <= 0 || acceleration >= totalMonths) return null;

    return { acceleration, eob: totalMonths - acceleration };
  };

  let accelerationBenefitDurationMonths: number | null = null;
  let eobBenefitDurationMonths: number | null = null;
  if (usesDurationDerivedPools) {
    accelerationBenefitDurationMonths = parseBenefitDurationMonths(quote.aobDuration);
    eobBenefitDurationMonths = parseBenefitDurationMonths(quote.cobDuration);
  } else if (isSecureCare) {
    const derived = deriveSecureCareDurations();
    accelerationBenefitDurationMonths = derived?.acceleration ?? null;
    eobBenefitDurationMonths = derived?.eob ?? null;
  }

  // A quote whose premium is billed over a fixed number of years (5-Pay, 10-Pay,
  // 20-Pay, ...) is an annual cost, not a single lump sum. Single-pay/annuity
  // quotes have no such field and fall back to the lump-sum branch below.
  const payYears = getQuotePayYears(quote);

  // For annuity products (like EquiTrust Bridge), use ltcBenefitTotal as the maximum benefit
  // since they don't have a death benefit
  const policyTypeDef = policyType ? fundingPolicyTypeDefs[policyType] : undefined;
  const isAnnuityProduct =
    policyTypeDef?.calculationType === FundingPolicyType.annuityHybrid ||
    policyType === FundingPolicyType.annuityHybrid;
  const maximumBenefitAmount = isAnnuityProduct ? quote.ltcBenefitTotal : quote.deathBenefitInitial;

  const basicPolicyFundingSource: PolicyFundingSource = {
    hasPolicyFunding: true,
    policyType,
    isSimpleInflationProtection: isSimpleInflation,
    isIndemnityPolicyPayment: getIsIndemnityPolicyPayment(quote),
    policyBenefitIsUnlimited: isUnlimitedPolicy,
    policyTotalLTCBenefit: isUnlimitedPolicy ? null : quote.ltcBenefitTotal,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
    addedViaShopInsurance: true,
    insuranceQuoteId: quote.insuranceQuoteId,
    policyAddedAt: Date.now(),
    policyLumpSumPayment: payYears !== null ? null : quote.premiumAmount,
    policyPremiumMonthlyCost: payYears !== null ? quote.premiumAmount / 12 : 0,
    policyPremiumStartYear: new Date().getFullYear(),
    policyMaximumBenefitAmount: maximumBenefitAmount,
    dailyHomeBenefitAmount: dailyBenefit,
    dailyHomeInflationProtectionPercent: inflationRate / 100,
    dailyAssistedLivingBenefitAmount: dailyBenefit,
    dailyAssistedLivingInflationProtectionPercent: inflationRate / 100,
    dailyIndependentLivingBenefitAmount: dailyBenefit,
    dailyIndependentLivingInflationProtectionPercent: inflationRate / 100,
    dailyNursingHomeBenefitAmount: dailyBenefit,
    dailyNursingHomeInflationProtectionPercent: inflationRate / 100,
    homeCareWaitingPeriodDays: resolveHomeCareWaitingPeriodDays(quote),
    facilityCareWaitingPeriodDays: resolveFacilityCareWaitingPeriodDays(quote),
    hasRetroactiveWaitingPeriodReimbursement: defaultsRetroactiveWaitingPeriodReimbursement(
      policyType,
    )
      ? true
      : null,
    policyLimitedPayYears: payYears,
    // CareMatters Annuity needs the period for more than sizing: the strategy
    // recovers the risk class from `period / accelerationMonths` (the 2x/3x
    // multiple) to pick the rider charge rate, because risk class rides
    // `primaryTobaccoStatus` on the quote and does not survive onto a policy.
    policyBenefitPeriodMonths:
      isCareMattersFamily(policyType) || isCareMattersAnnuity(policyType)
        ? (quote.ltcBenefitDurationMonths ?? null)
        : null,
    policyAccelerationBenefitDurationMonths: careMattersAnnuityAccelerationMonths(
      policyType,
      accelerationBenefitDurationMonths,
    ),
    // The contract value is the annuity's engine — the monthly cap is that value
    // over the acceleration window, and the death benefit is whatever of it care
    // does not consume. `deathBenefitInitial` holds it on these quote rows.
    policyAccumulatedValueAtIssue: isCareMattersAnnuity(policyType)
      ? (quote.deathBenefitInitial ?? null)
      : undefined,
    policyEobBenefitDurationMonths: eobBenefitDurationMonths,
    policyMonthlyWithdrawalRate: null,
    policyPremiumIncreaseRate: null,
    policyPremiumIncreaseYears: null,
    policyInflationProtection: calculatePolicyInflationProtection(
      inflationRate,
      quote.aobInflation,
      quote.cobInflation,
    ),
    policyInflationProtectionOnFaceAmount: aobInflationParsed
      ? aobInflationParsed / 100
      : cobInflationParsed !== null
        ? 0
        : null,
    policyInflationProtectionOnCOBAmount: cobInflationParsed ? cobInflationParsed / 100 : null,
    policyInflationProtectionYears: inflationProtectionYears,
    policyBenefitUtilizedToDate: 0,
    policyContinuationBenefitAmount: isUnlimitedPolicy
      ? null
      : quote.ltcBenefitTotal - (quote.deathBenefitInitial ?? 0),
    policyMinimumGuaranteedBenefitAmount:
      quote.guaranteedAccumulationValue ?? quote.deathBenefitMinimumGuaranteedIndividual ?? null,
    // Rated state, so the policy keeps its form set if the client moves.
    issuedInState: toStateAbbreviation(quote.usaState) || null,
    customPolicyId: null,
    sourceQuote: quote,
    updatedQuote: updatedQuote || null,
    illustrationPdfUrl: illustrationPdfUrl || null,
    // Include ROI values from updatedQuote if available
    policyProjectedReturnOnInvestment:
      updatedQuote?.roiValues?.projectedReturnOnInvestment ?? undefined,
    policyProjectedCompoundAnnualGrowthRate:
      updatedQuote?.roiValues?.projectedCompoundAnnualGrowthRate ?? undefined,
    policyProjectedInternalRateOfReturn:
      updatedQuote?.roiValues?.projectedInternalRateOfReturn ?? undefined,
    investmentPeriodYears: updatedQuote?.roiValues?.investmentPeriodYears ?? undefined,
  } as PolicyFundingSource;

  return basicPolicyFundingSource;
};

/**
 * Calculates ROI for a policy based on calculation type
 */
export const calculateROI = (calculatedPolicy: PolicyFundingSource, calculationType = 'total') => {
  let roi = 0;

  switch (calculationType) {
    case 'compoundAnnualGrowthRate':
      roi = calculatedPolicy.policyProjectedCompoundAnnualGrowthRate || 0;
      break;
    case 'internalRateOfReturn':
      roi = calculatedPolicy.policyProjectedInternalRateOfReturn || 0;
      break;
    case 'total':
    default:
      roi = calculatedPolicy.policyProjectedReturnOnInvestment || 0;
  }

  return roi;
};

export const createUpdatedQuoteFromResponse = (
  sourceQuote: Quote,
  responseData: { [key: string]: string | null } | null,
): Quote => {
  if (!responseData) {
    return sourceQuote;
  }

  // Parse monetary values from formatted strings (e.g., "$6,855" -> 6855)
  const parseMonetaryValue = (value: string | null): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/[$,]/g, '')) || 0;
  };

  const updatedQuote: Quote = {
    ...sourceQuote,
    premiumAmount: responseData.premiumAmount
      ? parseMonetaryValue(responseData.premiumAmount)
      : sourceQuote.premiumAmount,

    ltcBenefitMonthlyIndividual: responseData.initialMonthlyBenBase
      ? parseMonetaryValue(responseData.initialMonthlyBenBase)
      : sourceQuote.ltcBenefitMonthlyIndividual,

    ltcBenefitAnnualIndividual: responseData.initialAnnualBenBase
      ? parseMonetaryValue(responseData.initialAnnualBenBase)
      : sourceQuote.ltcBenefitAnnualIndividual,

    deathBenefitInitial: responseData.deathBenefit
      ? parseMonetaryValue(responseData.deathBenefit)
      : sourceQuote.deathBenefitInitial,

    ltcBenefitTotal:
      responseData.totalInitialLTC === 'Unlimited'
        ? 0 // 0 indicates unlimited in our system
        : responseData.totalInitialLTC
          ? parseMonetaryValue(responseData.totalInitialLTC)
          : sourceQuote.ltcBenefitTotal,

    aobInflation: responseData.aobInflOption || sourceQuote.aobInflation,
    cobInflation: responseData.cobInflOption || sourceQuote.cobInflation,
    inflationDuration: responseData.inflDuration || sourceQuote.inflationDuration,
  };

  return updatedQuote;
};

export const createQuoteWithROIandProjectedCoverage = (
  quote: Quote,
  client: Client,
  calculationType = 'total',
) => {
  const basicPolicy = createPolicyFundingSourceFromQuote(quote, client);
  const tempFundingSourceId = `temp-quote-preview-${quote.insuranceQuoteId}`;

  const clientClone = cloneDeep(client);

  clientClone.multipleFundingSources.ltcPolicy = {
    ...clientClone.multipleFundingSources.ltcPolicy,
    [tempFundingSourceId]: basicPolicy,
  };

  // The engine prices every policy it can route. For products in
  // `requiresClaimProjectionEngine` a missing projection is fatal rather than a
  // fallback: refuse to report a number instead of dropping to the legacy
  // approximation, which for CareMatters Annuity models one pool instead of the
  // acceleration + extension layers and understates the benefit by half or more.
  // Same fail-closed shape the short-term-care preview below uses.
  const mustBePricedByEngine = requiresClaimProjectionEngine(basicPolicy.policyType);
  const updatedClientWithDerivatives = calcClientDerivatives(
    clientClone,
    null,
  );

  if (
    mustBePricedByEngine &&
    !updatedClientWithDerivatives.policyClaimProjections?.[tempFundingSourceId]
  ) {
    throw new Error(
      `Claim projection missing for quote ${tempFundingSourceId}; ` +
        'refusing to report a legacy approximation.',
    );
  }

  const policy = updatedClientWithDerivatives.multipleFundingSources.ltcPolicy[tempFundingSourceId];
  const roi = calculateROI(policy, calculationType);
  // Indemnity cash applied to room & board is real coverage of the client's
  // total cost, and `useCoverageGap` divides this by the FULL cost (care + room
  // & board). Leaving it out understated Independent Living indemnity quotes.
  // 0 for reimbursement and for every non-Independent-Living client.
  const projectedCostCoverage =
    (policy.projectedCostCoverage || policy.policyCoverageOfRemainingLtcCosts || 0) +
    (policy.policyRoomAndBoardCovered ?? 0);

  const maxAchievableCoverage = calcMaxAchievableCoverage(
    updatedClientWithDerivatives,
    tempFundingSourceId,
    projectedCostCoverage,
  );

  const discountSavings =
    updatedClientWithDerivatives.allPhaseCosts
      ?.allPhaseInflatedProfessionalShareCostDiscountAmount || 0;

  const discountPolicyType = findDiscountPolicyType(
    updatedClientWithDerivatives.clientLocalCareEnvironmentCosts,
  );

  const roiValues = {
    projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment || 0,
    projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate || 0,
    projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
    investmentPeriodYears: policy.investmentPeriodYears || 0,
  };

  return {
    ...quote,
    roi,
    roiValues,
    projectedCostCoverage,
    maxAchievableCoverage,
    discountSavings,
    discountPolicyType,
  };
};

const getPremiumCostPerMonth = (quote: TraditionalLTCQuote): number => {
  switch (quote.premiumFrequency) {
    case 'Monthly':
      return quote.primaryModalPremium;
    case 'Annually':
      return quote.primaryModalPremium / 12;
    default:
      return quote.primaryModalPremium / 12;
  }
};

export const parseBioPercentToInflationRate = (bioPercent: string): number => {
  return bioPercent === 'No BIO' ? 0 : parseFloat(bioPercent.match(/(\d+)%/)?.[1] || '0') / 100;
};

export const createPolicyFundingSourceFromTraditionalLTCQuote = (
  quote: TraditionalLTCQuote,
  client: Client,
  isAdvisorSession = false,
  advisorId?: string,
): PolicyFundingSource => {
  const inflationRate = parseBioPercentToInflationRate(quote.bioPercent);
  const policyType = getPolicyTypeFromProductName(quote.productName, false);

  const basicPolicy = {
    carrierName: quote.carrier,
    hasPolicyFunding: true,
    isSimpleInflationProtection: false,
    addedByAdvisorId: isAdvisorSession ? advisorId : undefined,
    isIndemnityPolicyPayment: false,
    policyBenefitIsUnlimited: false,
    policyMaximumBenefitAmount: quote.benefitPool,
    policyPremiumMonthlyCost: getPremiumCostPerMonth(quote),
    policyPremiumStartYear: new Date().getFullYear(),
    policyInflationProtection: inflationRate,
    policyLumpSumPayment: null,
    dailyHomeBenefitAmount: quote.dailyBenefitAmount,
    dailyHomeInflationProtectionPercent: null,
    dailyIndependentLivingBenefitAmount: quote.dailyBenefitAmount,
    dailyIndependentLivingInflationProtectionPercent: null,
    dailyAssistedLivingBenefitAmount: quote.dailyBenefitAmount,
    dailyAssistedLivingInflationProtectionPercent: null,
    dailyNursingHomeBenefitAmount: quote.dailyBenefitAmount,
    dailyNursingHomeInflationProtectionPercent: null,
    homeCareWaitingPeriodDays: quote.eliminationPeriod,
    facilityCareWaitingPeriodDays: quote.eliminationPeriod,
    hasRetroactiveWaitingPeriodReimbursement: defaultsRetroactiveWaitingPeriodReimbursement(
      policyType,
    )
      ? true
      : null,
    policyLimitedPayYears: null,
    policyBenefitPeriodMonths: null,
    policyMonthlyWithdrawalRate: null,
    policyPremiumIncreaseRate: null,
    policyPremiumIncreaseYears: null,
    policyInflationProtectionOnFaceAmount: null,
    policyInflationProtectionOnCOBAmount: null,
    policyInflationProtectionYears: null,
    policyBenefitUtilizedToDate: 0,
    policyContinuationBenefitAmount: null,
    policyMinimumGuaranteedBenefitAmount: null,
    // Traditional LTC quotes carry the rated state as `state`, not `usaState`.
    issuedInState: toStateAbbreviation(quote.state) || null,
    customPolicyId:
      quote.traditionalLTCQuoteId != null ? String(quote.traditionalLTCQuoteId) : undefined,
    addedViaShopInsurance: false,
    insuranceQuoteId:
      quote.traditionalLTCQuoteId != null ? String(quote.traditionalLTCQuoteId) : undefined,
    sourceQuote: quote,
    policyType,
  };

  return basicPolicy as PolicyFundingSource;
};

export const createTraditionalLTCQuoteWithROIandProjectedCoverage = (
  quote: TraditionalLTCQuote,
  client: Client,
  calculationType = 'total',
) => {
  const basicPolicy = createPolicyFundingSourceFromTraditionalLTCQuote(quote, client);
  const tempFundingSourceId = `temp-trad-ltc-preview-${quote.traditionalLTCQuoteId}`;

  const clientClone = cloneDeep(client);

  clientClone.multipleFundingSources.ltcPolicy = {
    [tempFundingSourceId]: basicPolicy,
  };

  const updatedClientWithDerivatives = calcClientDerivatives(clientClone, null);

  const policy = updatedClientWithDerivatives.multipleFundingSources.ltcPolicy[tempFundingSourceId];
  const roi = calculateROI(policy, calculationType);
  // Indemnity cash applied to room & board is real coverage of the client's
  // total cost, and `useCoverageGap` divides this by the FULL cost (care + room
  // & board). Leaving it out understated Independent Living indemnity quotes.
  // 0 for reimbursement and for every non-Independent-Living client.
  const projectedCostCoverage =
    (policy.projectedCostCoverage || policy.policyCoverageOfRemainingLtcCosts || 0) +
    (policy.policyRoomAndBoardCovered ?? 0);

  const maxAchievableCoverage = calcMaxAchievableCoverage(
    updatedClientWithDerivatives,
    tempFundingSourceId,
    projectedCostCoverage,
  );

  // Extract discount savings from allPhaseCosts
  const discountSavings =
    updatedClientWithDerivatives.allPhaseCosts
      ?.allPhaseInflatedProfessionalShareCostDiscountAmount || 0;

  // Extract discount policy type from any care environment that has a discount
  const discountPolicyType = findDiscountPolicyType(
    updatedClientWithDerivatives.clientLocalCareEnvironmentCosts,
  );

  const roiValues = {
    projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment || 0,
    projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate || 0,
    projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
    investmentPeriodYears: policy.investmentPeriodYears || 0,
  };

  return {
    ...quote,
    roi,
    roiValues,
    projectedCostCoverage,
    maxAchievableCoverage,
    discountSavings,
    discountPolicyType,
  };
};

/**
 * Projected LTC coverage and ROI for a stored short-term care quote.
 *
 * Same shape as the traditional-LTC preview above — build a funding source,
 * inject it into a client clone under a temporary id, run the derivative pass,
 * read the coverage back — with one deliberate difference: a missing projection
 * is fatal here rather than a fallback.
 *
 * The legacy path approximates OmniFlex as generic short-term care indemnity: no
 * accrued-daily-benefit ramp, no per-track day counters, no restoration.
 * Coverage computed that way would be wrong, and wrong quietly, so the absence
 * of a projection drops the figure instead of displaying the approximation.
 */
export const createShortTermCareQuoteWithROIandProjectedCoverage = (
  quote: ShortTermCareQuote,
  client: Client,
  calculationType = 'total',
) => {
  const basicPolicy = createShortTermCareFundingSource(quote, undefined, false);
  const tempFundingSourceId = `temp-stc-preview-${quote.shortTermCareQuoteId}`;

  const clientClone = cloneDeep(client);

  clientClone.multipleFundingSources.ltcPolicy = {
    [tempFundingSourceId]: basicPolicy as PolicyFundingSource,
  };

  const updatedClientWithDerivatives = calcClientDerivatives(clientClone, null);

  // runPolicyCalculations swallows a strategy failure with console.error and
  // falls through to the LEGACY coverage path, which approximates OmniFlex as
  // generic short-term care indemnity — no ramp, no restoration, no day
  // counters. That degraded number is indistinguishable from the real one on
  // the card, so the absence of a projection is treated as a failure here and
  // the caller drops the figure instead of displaying the approximation.
  const policy = updatedClientWithDerivatives.multipleFundingSources.ltcPolicy[tempFundingSourceId];

  // Gate on what actually PRICED the policy, not on whether a projection exists.
  // The shadow run populates `policyClaimProjections` before the engine/legacy
  // decision, so with the brake pulled a projection is present while legacy
  // supplied the numbers — and this guard would have waved through exactly the
  // approximation it exists to suppress.
  if (policy?.policyProjectionSource !== 'engine') {
    throw new Error(
      `Claim projection missing for short-term care quote ${quote.shortTermCareQuoteId}; ` +
        'refusing to report legacy-approximated coverage',
    );
  }
  const roi = calculateROI(policy, calculationType);
  // Indemnity cash applied to room & board is real coverage of the client's
  // total cost, and `useCoverageGap` divides this by the FULL cost (care + room
  // & board). Leaving it out understated Independent Living indemnity quotes.
  // 0 for reimbursement and for every non-Independent-Living client.
  const projectedCostCoverage =
    (policy.projectedCostCoverage || policy.policyCoverageOfRemainingLtcCosts || 0) +
    (policy.policyRoomAndBoardCovered ?? 0);

  const roiValues = {
    projectedReturnOnInvestment: policy.policyProjectedReturnOnInvestment || 0,
    projectedCompoundAnnualGrowthRate: policy.policyProjectedCompoundAnnualGrowthRate || 0,
    projectedInternalRateOfReturn: policy.policyProjectedInternalRateOfReturn,
    investmentPeriodYears: policy.investmentPeriodYears || 0,
    // Carried through so formatROI can mark a degenerate IRR as suspect rather
    // than printing it as fact — likelier here than on level-premium products,
    // since a ramped, day-capped benefit can produce odd cashflow shapes.
    isIrrSuspect: policy.isIrrSuspect,
  };

  return {
    ...quote,
    roi,
    roiValues,
    projectedCostCoverage,
  };
};
